import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  AccountNumberConfiguration,
  AccountNumberConfigurationUpdateRequest,
} from './account-number-configuration-api.types';
import {
  ACCOUNT_SEGMENT_COUNT_MAX,
  ACCOUNT_SEGMENT_COUNT_MIN,
} from '@system-dyg/shared/utils';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const CONFIGURATION_PATH = '/general/account-number-configuration';

@Injectable({ providedIn: 'root' })
export class AccountNumberConfigurationApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${CONFIGURATION_PATH}`;

  /** `null` cuando el GET 200 no trae `data`: la estructura todavía no existe. */
  get(): Promise<AccountNumberConfiguration | null> {
    return this.read(
      this.http.get<unknown>(this.baseUrl).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => retryDelay(error) }),
        map(body => unwrapConfiguration(body)),
        catchError(error => throwError(() => mapConfigurationError(error))),
      ),
    );
  }

  save(
    payload: AccountNumberConfigurationUpdateRequest,
  ): Promise<AccountNumberConfiguration> {
    return this.request(
      this.http.put<unknown>(this.baseUrl, {
        segmentCount: payload.segmentCount,
      }),
      body => unwrapRequiredConfiguration(body),
    );
  }

  private async read<T>(source: Observable<T>): Promise<T> {
    try {
      return await firstValueFrom(source);
    } catch (error) {
      throw mapConfigurationError(error);
    }
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapConfigurationError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function mapConfigurationError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = stringField(body, ['error']);
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function unwrapConfiguration(body: unknown): AccountNumberConfiguration | null {
  const data = readData(body);
  if (data === undefined || data === null) return null;
  return normalizeConfiguration(data);
}

function unwrapRequiredConfiguration(body: unknown): AccountNumberConfiguration {
  const data = readData(body);
  if (data === undefined || data === null) {
    throw new SettingsApiError('unexpected', 0);
  }
  return normalizeConfiguration(data);
}

function normalizeConfiguration(raw: unknown): AccountNumberConfiguration {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const separator = stringField(record, ['separator']);
  const segmentCount = record?.['segmentCount'];
  if (
    !uuid ||
    !separator ||
    typeof segmentCount !== 'number' ||
    !Number.isInteger(segmentCount) ||
    segmentCount < ACCOUNT_SEGMENT_COUNT_MIN ||
    segmentCount > ACCOUNT_SEGMENT_COUNT_MAX
  ) {
    throw new SettingsApiError('unexpected', 0);
  }
  return { uuid, segmentCount, separator };
}

function readData(body: unknown): unknown | undefined {
  const record = asRecord(body);
  if (!record || !('data' in record)) return undefined;
  return record['data'];
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(
  record: Record<string, unknown> | null,
  keys: string[],
): string {
  if (!record) return '';
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}
